import { Button, Form, Modal, Select } from "antd";
import { useForm } from "antd/lib/form/Form";
import CIQDatePicker from "components/custom-date-picker";
import { SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DateUtils } from "utils/dateutils";

function FilterForm({
  open,
  onFinish,
  onCancel,
  onBack,
  closable
}: {
  open: boolean;
  closable: boolean;
  onFinish: (e: any) => void;
  onBack: (e: any) => void;
  onCancel: any;
}) {
  const {
    columnHeaders: { materialHeaderMap, submittalHeaderMap }
  } = useContext(ProjectContext);

  const [form] = useForm();
  const [customDatePicker, showCustomDatePicker] = useState(false);
  const [customDate, setCustomDate] = useState<any>();
  const customDateRef = useRef(customDate);
  customDateRef.current = customDate;

  const filterOptions = useMemo(() => {
    const readableCustomDate: string = customDate
      ? `(Due by ${DateUtils.format(customDate)})`
      : "";
    return [
      {
        value: DateUtils.dateTimeObj().startOf("day").add(14, "days").valueOf(),
        label: "Due in 2 weeks"
      },
      {
        value: DateUtils.dateTimeObj().startOf("day").valueOf(),
        label: "Past Due date"
      },
      { value: "custom", label: `Custom ${readableCustomDate}` }
    ];
  }, [customDate]);

  const reportFor = Form.useWatch("reportFor", form);

  useEffect(() => {
    form.resetFields(["groupby"]);
  }, [form, reportFor]);

  const resetFormState = () => {
    showCustomDatePicker(false);
    setCustomDate("");
    form.resetFields();
  };

  const formLabels: any = useMemo(() => {
    return {
      submittal: {
        due_date: submittalHeaderMap.due_date
      },
      material: {
        due_date: materialHeaderMap.due_date
      }
    };
  }, [materialHeaderMap.due_date, submittalHeaderMap.due_date]);

  return (
    <Modal
      className="custom-drawer"
      title="Upcoming Due Dates Review"
      width={420}
      style={SidepanelStyles.style}
      data-testid="upcoming-filter-modal"
      footer={
        <div className="flex w-full justify-end space-x-4">
          {!closable && (
            <Button
              form="upcomingDueDateForm"
              htmlType="reset"
              className="px-5"
              onClick={onBack}
              data-testid="upcoming-back"
            >
              Back
            </Button>
          )}
          {closable && (
            <Button
              form="upcomingDueDateForm"
              htmlType="reset"
              className="px-5"
              onClick={onCancel}
              data-testid="upcoming-close"
            >
              Close
            </Button>
          )}
          <Button
            form="upcomingDueDateForm"
            htmlType="reset"
            className="px-5"
            onClick={resetFormState}
            data-testid="upcoming-reset"
          >
            Reset
          </Button>
          <Button
            form="upcomingDueDateForm"
            type="primary"
            htmlType="submit"
            className="px-5"
            disabled={!reportFor || customDatePicker}
            data-testid="upcoming-run"
          >
            Run Report
          </Button>
        </div>
      }
      open={open}
      closable={closable}
      onCancel={onCancel}
      destroyOnHidden
      mask={{ closable: false }}
      keyboard={false}
    >
      <div className="px-3 mt-0">
        <div className="text-xs mb-3">
          Select the below options to view report.
        </div>

        <Form
          id="upcomingDueDateForm"
          form={form}
          layout="vertical"
          className=""
          onFinish={(e) => {
            const values = e as any;
            if (e.filter === "custom") {
              values.filter = customDate ? customDate.valueOf() : "";
            }
            onFinish(values);
          }}
        >
          <div>
            <Form.Item name="reportFor" label="Select Item Type" required>
              <Select
                options={[
                  { value: "submittal", label: "Submittals" },
                  { value: "material", label: "Materials" }
                ]}
                placeholder="Select Item Type"
                data-testid="upcoming-report-for"
              />
            </Form.Item>
          </div>

          {reportFor && (
            <div>
              <div className="relative">
                <Form.Item
                  name="filter"
                  label="Due Date"
                  initialValue={filterOptions[0].value}
                  required
                >
                  <Select
                    options={filterOptions}
                    data-testid="upcoming-due-filter"
                    onSelect={(v: any) => {
                      setTimeout(() => {
                        if (v === "custom") {
                          showCustomDatePicker(true);
                        } else {
                          showCustomDatePicker(false);
                          setCustomDate("");
                        }
                      });
                    }}
                    onClick={() => {
                      showCustomDatePicker(false);
                    }}
                  />
                </Form.Item>
                <CIQDatePicker
                  className="absolute h-0 w-0 overflow-visible p-0 m-0 border-0 ciq-date-picker"
                  open={customDatePicker}
                  value={customDate}
                  getPopupContainer={(trigger: HTMLElement) =>
                    trigger.parentElement ?? trigger
                  }
                  onOpenChange={(nextOpen: boolean) => {
                    if (nextOpen) return;
                    showCustomDatePicker(false);
                    // onChange and onOpenChange can fire in either order after a
                    // cell click; wait a tick so a just-picked date is not treated
                    // as a cancel.
                    setTimeout(() => {
                      if (
                        form.getFieldValue("filter") === "custom" &&
                        !customDateRef.current
                      ) {
                        form.setFieldValue("filter", filterOptions[0].value);
                      }
                    }, 0);
                  }}
                  onChange={(v) => {
                    customDateRef.current = v;
                    setCustomDate(v);
                    showCustomDatePicker(false);
                  }}
                />
                {reportFor === "submittal" && (
                  <Form.Item
                    name="groupby"
                    label="GROUP BY"
                    initialValue="RC_GroupBy"
                    required
                  >
                    <Select
                      options={[
                        {
                          value: "RC_GroupBy",
                          label: submittalHeaderMap.responsible_contractor
                        },
                        {
                          value: "BIC_GroupBy",
                          label: submittalHeaderMap.assignee
                        }
                      ]}
                      disabled={customDatePicker}
                      data-testid="upcoming-submittal-group-by"
                    />
                  </Form.Item>
                )}
                {reportFor === "material" && (
                  <Form.Item
                    name="groupby"
                    label="GROUP BY"
                    initialValue="GroupBy_RC"
                    required
                  >
                    <Select
                      options={[
                        {
                          value: "GroupBy_RC",
                          label: materialHeaderMap.trade_partner
                        },
                        {
                          value: "GroupBy_GCR",
                          label: materialHeaderMap.gc_representative
                        }
                      ]}
                      disabled={customDatePicker}
                      data-testid="upcoming-material-group-by"
                    />
                  </Form.Item>
                )}
                <Form.Item
                  name="sortBy"
                  label="SORT BY"
                  initialValue="dueDate"
                  required
                >
                  <Select
                    options={[
                      {
                        value: "dueDate",
                        label: formLabels[reportFor].due_date
                      },
                      { value: "risk", label: "Risk" }
                    ]}
                    disabled={customDatePicker}
                    data-testid="upcoming-sort-by"
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </Form>
      </div>
    </Modal>
  );
}
export default FilterForm;
